import { Action, configureStore, ThunkAction } from '@reduxjs/toolkit';
import { createMigrate, PersistConfig, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import macBotReducer from '../components/dashboard/knowledge-bases-create/slice/macBotSlice';
import appearanceReducer from '../features/appearance/appearanceSlice';
import IAuthState from '../features/auth/auth.slice';
import authReducer from '../features/auth/auth.slice';
import botReducer, { InitialState } from '../features/bot/botSlice';
import dashboardReducer from '../features/dashboard.slice';
import knowledgeReducer from '../features/knowledge.slice';
import scenarioReducer from '../features/scenario.slice';
import createBotFlowReducer from '../features/scenario/create/create.slice';
import sumReducer from '../features/sum/sum.slice';
import uiReducer from '../features/ui/uiSlice';
import IKnowledgeState from '../interfaces/models/Knowledge/knowledge-state.interface';
import { RtkQueryErrorLogger } from './middlewares/rtk-query-error-logger.middleware';
import { SumSliceMiddleware } from './middlewares/sum-middleware';
import { AuthRTKService } from './services/auth-rtk.service';
import { BillingRTKService } from './services/billing-rtk.service';
import { ChatbotRTKService } from './services/chatbot-rtk.services';
import { PackageRTKService } from './services/package-rtk.service';
import { ScenarioRTKService } from './services/scenario-rtk.service';
import { SUMRTKService } from './services/sum-rtk.service';

const authPersistConfig = {
  key: 'auth',
  blacklist: ['error', 'profile'],
  storage,
};

const knowledgePersistConfig = {
  key: 'knowledge',
  storage,
  whitelist: ['create'],
  version: 1,
  migrate: createMigrate(
    {
      1: (state: any) => {
        const { openAiAPIKey, ...create } = state?.create ?? {};
        return Promise.resolve({ ...state, create });
      },
    },
    { debug: false }
  ),
} as PersistConfig<IKnowledgeState>;

const sumPersistConfig = {
  key: 'sumRTK',
  storage,
};
const persistedSumReducer = persistReducer(sumPersistConfig, sumReducer);

const createBotFlowPersistConfig = {
  key: 'createBotFlow',
  storage,
};
const persistedCreateBotFlowReducer = persistReducer(createBotFlowPersistConfig, createBotFlowReducer);

// Bots persist configs
const botsPersistConfig = {
  key: 'botsPersisted',
  version: 0,
  storage,
  migrate: state => {
    // TODO ==> Migration logic written for any future state upgrades and modifications
    // console.debug("Migration Running!");

    return Promise.resolve(state);
  },
} as PersistConfig<InitialState>;

export const store = configureStore({
  reducer: {
    authState: persistReducer<IAuthState, any>(authPersistConfig, authReducer),
    dashboardState: dashboardReducer,
    scenarioState: scenarioReducer,
    knowledgestate: persistReducer<IKnowledgeState, any>(knowledgePersistConfig, knowledgeReducer),
    bots: persistReducer<InitialState, any>(botsPersistConfig, botReducer),
    appearance: appearanceReducer,
    ui: uiReducer,
    // RTK
    [AuthRTKService.reducerPath]: AuthRTKService.reducer,
    [ChatbotRTKService.reducerPath]: ChatbotRTKService.reducer,
    [BillingRTKService.reducerPath]: BillingRTKService.reducer,
    [ScenarioRTKService.reducerPath]: ScenarioRTKService.reducer,
    [PackageRTKService.reducerPath]: PackageRTKService.reducer,
    sumState: persistedSumReducer,
    [SUMRTKService.reducerPath]: SUMRTKService.reducer,
    createBotFlow: persistedCreateBotFlowReducer,
    macBot: macBotReducer,
  },
  devTools: import.meta.env.VITE_NODE_ENV === 'development',
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat([
      AuthRTKService.middleware,
      ChatbotRTKService.middleware,
      BillingRTKService.middleware,
      ScenarioRTKService.middleware,
      SUMRTKService.middleware,
      PackageRTKService.middleware,
      SumSliceMiddleware,
      RtkQueryErrorLogger,
    ]),
});

export const persistor = persistStore(store);

export const resetStore = () => {
  return new Promise<void>((resolve, reject) => {
    try {
      persistor.pause();

      persistor.flush().then(async () => {
        return await persistor.purge();
      });

      return resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export const resetSlice = (sliceKey: string) => {
  return new Promise<void>((resolve, reject) => {
    try {
      persistor.pause();

      persistor.flush().then(() => {
        const storageKey = `persist:${sliceKey}`;
        localStorage.removeItem(storageKey);
        return persistor.persist();
      });

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
